import React, { useState } from 'react';
import Tippy from '@tippyjs/react';
import { Typography } from '../Typography/Typography';
import Button from '../Button/Button';

export interface NuxProps {
  /** The element the NUX is attached to (always visible) */
  children: React.ReactElement;
  /** Whether the NUX tooltip is shown */
  showNux: boolean;
  /** Called when the dismiss button is clicked */
  onDismiss: () => void;
  /** Title text displayed in the NUX */
  title: string;
  /** Description text displayed in the NUX */
  description: string;
  /** Text for the dismiss button */
  dismissButtonText: string;
}

const NUX_WIDTH = 228;
// Lower than Modal's z-index (1000) so modals appear on top
const NUX_Z_INDEX = 100;

// Tippy requires CSS selectors to style internal elements like the arrow.
// These styles cannot be expressed with Tailwind utility classes.
const tippyStyles = `
  .nux-tippy {
    background-color: var(--grayscale-900);
    border-radius: var(--border-radius-medium);
    padding: 10px 12px;
    width: ${NUX_WIDTH}px;
  }

  /* Style Tippy arrow - uses CSS border trick for the triangle */
  .nux-tippy .tippy-arrow {
    width: 16px;
    height: 16px;
  }

  .nux-tippy .tippy-arrow::before {
    content: '';
    position: absolute;
    border-color: transparent;
    border-style: solid;
  }

  /* Arrow positioning for bottom placement (arrow points up) */
  .nux-tippy[data-placement^='bottom'] > .tippy-arrow {
    top: 0;
    /* Override Tippy's auto-positioning to place arrow 24px from left edge */
    transform: translate3d(24px, 0px, 0px) !important;
  }

  .nux-tippy[data-placement^='bottom'] > .tippy-arrow::before {
    top: -7px;
    left: 0;
    border-width: 0 8px 8px;
    border-bottom-color: var(--grayscale-900);
    transform-origin: center bottom;
  }

  /* Enables opacity fade animation */
  .nux-tippy[data-state='hidden'] {
    opacity: 0;
  }
`;

/**
 * A New User Experience (NUX) tooltip component that displays an announcement
 * with a title, description, and dismiss button. Used to highlight new features
 * or UI changes to users.
 */
export const Nux: React.FC<NuxProps> = ({ children, showNux, onDismiss, title, description, dismissButtonText }) => {
  const [isButtonHovered, setIsButtonHovered] = useState(false);

  // Inline styles to override Button's styled-components (which have high specificity)
  const dismissButtonStyle: React.CSSProperties = {
    alignSelf: 'flex-end',
    backgroundColor: isButtonHovered ? 'var(--grayscale-50)' : 'var(--white)',
    color: 'var(--grayscale-900)',
    border: 'none',
  };

  const content = (
    <div className="flex flex-col gap-2.5">
      <Typography styledAs="bodyLargeSpaceGrotesk" weight={500} color="var(--white)">
        {title}
      </Typography>
      <Typography color="var(--white)">{description}</Typography>
      <Button
        size="sm"
        onClick={onDismiss}
        onMouseEnter={() => setIsButtonHovered(true)}
        onMouseLeave={() => setIsButtonHovered(false)}
        aria-label={`${dismissButtonText}, dismiss this notification`}
        style={dismissButtonStyle}
      >
        {dismissButtonText}
      </Button>
    </div>
  );

  return (
    <>
      <style>{tippyStyles}</style>
      <Tippy
        className="nux-tippy"
        content={content}
        visible={showNux}
        placement="bottom-start"
        arrow={true}
        offset={[0, 8]}
        interactive
        appendTo="parent"
        // HACK: Tippy sets z-index: 9999 inline on [data-tippy-root], which causes the NUX
        // to appear on top of modals (z-index: 1000). The zIndex prop and CSS overrides
        // don't work because Tippy applies the inline style after initialization.
        // This custom Popper modifier runs in the "write" phase (after Tippy's setup)
        // to override the z-index to a value lower than modals.
        popperOptions={{
          modifiers: [
            {
              name: 'zIndex',
              enabled: true,
              phase: 'write',
              fn: ({ state }) => {
                state.elements.popper.style.zIndex = String(NUX_Z_INDEX);
              },
            },
          ],
        }}
      >
        {children}
      </Tippy>
    </>
  );
};
